// THE CONSOLE'S NAVIGATION, by job (spec 15 §4; D-626).
//
// The left rail groups every console screen under the job it serves. The
// groups are fixed here, in this order; the ENTRIES are a registry that each
// M15 lane appends to when it adds a screen (the route contract is at the top
// of console-routes.tsx). A group with no entry this staff member may see is
// not drawn at all, so the rail never shows a heading over nothing.
//
// `visible` mirrors the route's server-side permission (spec 15 §7). It only
// decides whether the link is drawn; the API is the fence, and a screen whose
// read is refused still renders its own 403 state.

import type { PlatformMe } from "../lib/platform-api";
import { canReadAudit } from "../lib/support";

/** The rail's groups, top to bottom. Each has `admin.navGroups.<group>`. */
export const CONSOLE_NAV_GROUPS = [
  "fleet",
  "tenants",
  "support",
  "health",
  "usage",
  "staff",
  "account",
] as const;

export type ConsoleNavGroup = (typeof CONSOLE_NAV_GROUPS)[number];

export type ConsoleNavIcon =
  "overview" | "tenants" | "support" | "health" | "usage" | "staff" | "audit" | "account";

export interface ConsoleNavEntry {
  /** Unique; also the default test id suffix (`admin-nav-<key>`). */
  readonly key: string;
  readonly group: ConsoleNavGroup;
  /** An absolute console path, a route declared in console-routes.tsx. */
  readonly to: string;
  /** An i18n key in the admin catalogues. */
  readonly labelKey: string;
  readonly icon: ConsoleNavIcon;
  /** Lit only on `to` itself, not on paths under it. */
  readonly exact?: boolean;
  /** Who sees the link (the route's §7 rule); absent means every role. */
  readonly visible?: (me: PlatformMe) => boolean;
}

export const CONSOLE_NAV: readonly ConsoleNavEntry[] = [
  {
    key: "overview",
    group: "fleet",
    to: "/console",
    labelKey: "admin.nav.overview",
    icon: "overview",
    exact: true,
  },
  {
    key: "tenants",
    group: "tenants",
    to: "/console/tenants",
    labelKey: "admin.nav.tenants",
    icon: "tenants",
  },
  {
    key: "account",
    group: "account",
    to: "/console/account",
    labelKey: "admin.nav.account",
    icon: "account",
  },
  // M15-4 (D-611 to D-613): the fleet health board and the logs, every role.
  {
    key: "health",
    group: "health",
    to: "/console/health",
    labelKey: "admin.health.nav",
    icon: "health",
  },
  {
    key: "logs",
    group: "health",
    to: "/console/logs",
    labelKey: "admin.logs.nav",
    icon: "audit",
  },
  // M15-6 (D-615): the plan list, read by every role.
  {
    key: "plans",
    group: "usage",
    to: "/console/plans",
    labelKey: "admin.plans.nav",
    icon: "usage",
  },
  // M15-9 (D-621, D-622): staff and the audit log, for the roles that may
  // read them (platform_admin, platform_readonly).
  {
    key: "staff",
    group: "staff",
    to: "/console/staff",
    labelKey: "admin.opsNav.staff",
    icon: "staff",
    visible: (me) => canReadAudit(me.role),
  },
  {
    key: "audit",
    group: "staff",
    to: "/console/audit",
    labelKey: "admin.opsNav.audit",
    icon: "audit",
    visible: (me) => canReadAudit(me.role),
  },
  // ---- other lanes append their entries above this line ----
];

/** Is `entry` the screen the reader is on (or, unless exact, under it)? */
export function navEntryLit(entry: ConsoleNavEntry, pathname: string): boolean {
  const path = pathname.replace(/\/+$/u, "") || "/";
  if (path === entry.to) return true;
  return entry.exact !== true && path.startsWith(`${entry.to}/`);
}

/** The rail for `me`: groups in order, each with the entries `me` may see,
 * and no group left empty. */
export function consoleNavFor(
  me: PlatformMe,
  nav: readonly ConsoleNavEntry[] = CONSOLE_NAV,
): { group: ConsoleNavGroup; entries: ConsoleNavEntry[] }[] {
  return CONSOLE_NAV_GROUPS.map((group) => ({
    group,
    entries: nav.filter((e) => e.group === group && (e.visible === undefined || e.visible(me))),
  })).filter((g) => g.entries.length > 0);
}

/** Which deployment this console is (the top bar's badge), from the host. */
export type ConsoleEnvironment = "production" | "staging" | "local";

export function consoleEnvironment(hostname: string): ConsoleEnvironment {
  if (hostname.includes(".staging.")) return "staging";
  if (
    hostname === "localhost" ||
    hostname.endsWith(".localhost") ||
    hostname.startsWith("127.") ||
    hostname.endsWith(".local")
  ) {
    return "local";
  }
  return "production";
}
